// Design agent: Loads the shared Material palette and core layouts for the MasqIP suite.
// Developer agent: Ensures thematic variables are available before component styling executes.
import { useState, useEffect, useRef } from 'react';
import './styles/materialTheme.css';
import './App.css';
import './components/BelongMode.css';
import VlsmCalculator from './components/VlsmCalculator';
import IpInspector from './components/IpInspector';
import NavigationDock from './components/NavigationDock';
import About from './components/About';
import Showcase from './components/Showcase';
import ReferenceTable from './components/ReferenceTable';
import { Alert, AlertDescription } from './components/Alert';
import {
  createDefaultAppState,
  decodeSearch,
  encodeState,
  loadLocalState,
  saveLocalState,
  clearLocalState,
} from './utils/persistence';

// Design agent: Spec 011 — decides what state a fresh page load restores. The URL takes priority
// (opening a link a coworker shared must always reproduce exactly what they shared) over
// localStorage (a plain reload/reopen keeps whatever this browser last had open), which in turn
// takes priority over hard defaults.
// Developer agent: Reads `window.location.search` once, inside the lazy useState initialiser below
// (see the `bootState` declaration), so it only ever runs on the very first render. Every helper it
// calls (decodeSearch, loadLocalState) is already defensive on its own, but the whole function is
// still wrapped so a genuinely unexpected `window` shape degrades to defaults instead of a blank page.
function computeBootState() {
  try {
    if (window.location.search && window.location.search.length > 0) {
      return decodeSearch(window.location.search);
    }
  } catch (error) {
    // Design agent: Fall through to localStorage/defaults — an unreadable URL should never be
    // worse than simply not having one.
  }

  const fromLocalStorage = loadLocalState();
  if (fromLocalStorage) {
    // Developer agent: Merges over a fresh default state field-by-field (rather than trusting the
    // stored object outright) so a slightly older/different saved shape never leaves a field the
    // rest of the app expects to always exist as undefined.
    const defaults = createDefaultAppState();
    return {
      tab: fromLocalStorage.tab ?? defaults.tab,
      inspector: { ...defaults.inspector, ...fromLocalStorage.inspector },
      vlsm: { ...defaults.vlsm, ...fromLocalStorage.vlsm },
    };
  }

  return createDefaultAppState();
}

// Design agent: Spec 011's Share button needs the modern async Clipboard API with a graceful
// fallback for contexts where it is unavailable (a non-HTTPS origin, an older browser, a denied
// permission). This mirrors ExportMenu's own local `copyTextToClipboard` helper exactly; it is kept
// as a small separate copy here (rather than extracted into a shared utility) so this spec's change
// stays confined to the files it lists and never risks altering ExportMenu's already-shipped,
// already-tested export behaviour.
// Developer agent: Returns a boolean instead of throwing, exactly like ExportMenu's version, so the
// caller can show a friendly success/failure message either way.
async function copyTextToClipboard(text) {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    // Design agent: Fall through to the legacy fallback below rather than surfacing this error —
    // clipboard permission prompts/denials are common and not actionable by the user here.
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const succeeded = document.execCommand('copy');
    document.body.removeChild(textarea);
    return succeeded;
  } catch (error) {
    return false;
  }
}

// Design agent: Defensive component wrapper to avoid runtime crashes if a component import fails.
// Developer agent: Spec 003 adds an optional componentProps object so a mounted tool (IpInspector,
// ReferenceTable) can receive data/callbacks from App without every existing call site needing to
// change — componentProps defaults to an empty object and is simply spread onto the component.
// This is declared at module scope (not inside App) on purpose: a component function declared
// inside another component's body is re-created on every render of its parent, and React treats
// each new function reference as a brand-new component type — remounting (and resetting all state
// of) everything it wraps. Spec 003's onActivePrefixChange callback now causes App to re-render on
// every keystroke in the inspector, which would have silently reset IpInspector's internal state
// (focus, collapsed cards, belong mode, ...) on every character typed had SafeMount stayed inline.
const SafeMount = ({ component: Comp, name, componentProps = {} }) => {
  const isRenderable = typeof Comp === 'function';
  return isRenderable ? (
    <Comp {...componentProps} />
  ) : (
    <div className="error-text" id={`component-missing-${name}`}>
      Component "{name}" failed to load. Please reload.
    </div>
  );
};

// Design agent: Hosts the primary IPv4 tools inside a Material-inspired glassmorphism layout.
function App() {
  // Design agent: Spec 011 — the one-time boot decision (URL > localStorage > defaults). Computed
  // exactly once via useState's lazy-initialiser form; every piece of restorable state below reads
  // its own slice of this same object so the whole app boots from one consistent snapshot.
  const [bootState] = useState(computeBootState);

  const [activeTab, setActiveTab] = useState(bootState.tab);
  // Design agent: Spec 003 — remembers the prefix of the address currently being inspected so the
  // Reference table (a separate view) can highlight the matching row when the user switches to it.
  // Developer agent: null means "nothing valid is currently inspected"; ReferenceTable simply never
  // matches a row against null.
  const [activePrefix, setActivePrefix] = useState(null);

  // Design agent: Spec 011 — the latest persistable snapshot each tool has reported through its own
  // `onStateChange` callback. IpInspector/VlsmCalculator keep owning their state; these two mirror
  // just enough of it here for the debounced write effect below to have something to serialise.
  // Developer agent: Seeded from bootState so the very first write (before either tool reports any
  // change of its own) persists exactly what was just restored, not empty defaults.
  const [inspectorSnapshot, setInspectorSnapshot] = useState(bootState.inspector);
  const [vlsmSnapshot, setVlsmSnapshot] = useState(bootState.vlsm);

  // Design agent: Spec 011 — the Share button's confirmation message, shown as a floating toast near
  // the navigation controls and auto-dismissed a few seconds later.
  const [shareStatusMessage, setShareStatusMessage] = useState(null);
  const shareStatusTimeoutRef = useRef(null);

  // Design agent: Spec 011 — persists the combined snapshot to localStorage and mirrors it into the
  // URL (via history.replaceState, never pushState — sharing never grows the browser's back-button
  // history) 300ms after the last change, so a burst of keystrokes across either tool only costs one
  // write instead of one per character.
  // Developer agent: The cleanup function cancels a pending write whenever any dependency changes
  // again before the timeout fires, which is exactly what gives this its debounce behaviour.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const snapshot = { tab: activeTab, inspector: inspectorSnapshot, vlsm: vlsmSnapshot };
      saveLocalState(snapshot);
      const search = encodeState(snapshot);
      const nextUrl = `${window.location.pathname}${search ? `?${search}` : ''}`;
      window.history.replaceState(null, '', nextUrl);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [activeTab, inspectorSnapshot, vlsmSnapshot]);

  // Design agent: Spec 011 — clears any pending "copied" toast timeout on unmount so it never fires
  // a state update against an unmounted component.
  useEffect(() => {
    return () => {
      if (shareStatusTimeoutRef.current) {
        clearTimeout(shareStatusTimeoutRef.current);
      }
    };
  }, []);

  // Design agent: Spec 011 — shows the Share/Reset confirmation toast for a few seconds, replacing
  // any confirmation still on screen from a previous click.
  const showShareStatus = (message) => {
    setShareStatusMessage(message);
    if (shareStatusTimeoutRef.current) {
      clearTimeout(shareStatusTimeoutRef.current);
    }
    shareStatusTimeoutRef.current = setTimeout(() => setShareStatusMessage(null), 4000);
  };

  // Design agent: Spec 011 — copies a link that reproduces the current screen. Builds the URL
  // directly from the live snapshot (rather than trusting `window.location.href`) so a click right
  // after an edit is never behind the 300ms debounced history.replaceState above.
  const handleShare = async () => {
    const snapshot = { tab: activeTab, inspector: inspectorSnapshot, vlsm: vlsmSnapshot };
    const search = encodeState(snapshot);
    const shareUrl = `${window.location.origin}${window.location.pathname}${search ? `?${search}` : ''}`;
    const didCopy = await copyTextToClipboard(shareUrl);
    showShareStatus(didCopy ? 'Link copied to clipboard' : 'Copy failed — check clipboard permissions');
  };

  // Design agent: Spec 011 — wipes the persisted snapshot and reloads from a bare URL, returning to
  // hard defaults. A full reload (rather than resetting each piece of React state individually) is
  // deliberate: it guarantees every tool — including any state this component does not itself
  // mirror — restarts from exactly the same place a brand-new visit would.
  const handleReset = () => {
    clearLocalState();
    window.location.assign(window.location.pathname);
  };

  // Design agent: Automatically focus the first relevant input when the active view changes.
  // Developer agent: Queries specifically within the newly active card to avoid stealing focus on mount if not desired.
  useEffect(() => {
    // Small timeout to allow the transition/mount to complete
    const timer = setTimeout(() => {
      let selector = '';
      if (activeTab === 'inspector') {
        // Target the first input in the IpInspector
        selector = '#tool-card-inspector input:not([type="hidden"])';
      } else if (activeTab === 'plan') {
        // Target the first input in VlsmCalculator
        selector = '#tool-card-plan input:not([type="hidden"])';
      }

      if (selector) {
        const input = document.querySelector(selector);
        if (input) {
          input.focus();
        }
      }
    }, 150); // 150ms matches part of the transition duration

    return () => clearTimeout(timer);
  }, [activeTab]);

  return (
    <div className="app-surface" id="app-surface">
      <nav className="top-nav">
        <button className="top-nav-link" onClick={() => setActiveTab('about')}>About</button>
        <button className="top-nav-link" onClick={() => setActiveTab('showcase')}>Styles</button>
        {/* Design agent: Spec 011 — Share/Reset live in the top nav (the one place shared across
            every tab) rather than duplicated per-tool, since both act on the whole app's state at
            once, not just whichever tool is currently visible. */}
        <button
          className="top-nav-link"
          id="top-nav-share-button"
          onClick={handleShare}
          title="Copy a link that reproduces the current screen"
        >
          Share
        </button>
        <button
          className="top-nav-link"
          id="top-nav-reset-button"
          onClick={handleReset}
          title="Clear saved entries and return to defaults"
        >
          Reset
        </button>
        <a className="top-nav-link" href="https://github.com/WireSwarm/MasqIP" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.04-.015-2.04-3.33.72-4.035-1.605-4.035-1.605-.54-1.38-1.335-1.755-1.335-1.755-1.085-.735.09-.72.09-.72 1.2.075 1.83 1.23 1.83 1.23 1.065 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.475-1.335-5.475-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405 1.02 0 2.04.135 3 .405 2.28-1.56 3.285-1.23 3.285-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81.99.81 2.22 0 1.605-.015 2.895-.015 3.285 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
          </svg>
          <span className="hide-mobile">GitHub</span>
        </a>
      </nav>

      {/* Design agent: Spec 011 — a small floating confirmation for Share/Reset actions, reusing the
          existing Alert component (rather than inventing a new one) so it matches the rest of the
          app's glassmorphism styling out of the box. Only rendered while a message is pending. */}
      {shareStatusMessage && (
        <div className="share-status-toast" id="share-status-toast">
          <Alert id="share-status-alert">
            <AlertDescription id="share-status-alert-description">{shareStatusMessage}</AlertDescription>
          </Alert>
        </div>
      )}
      <header className="app-header" id="app-header">
        {/* Design agent: Introduced the hero branding stack pairing the logo with typographic hierarchy. */}
        <div className="brand-identity" id="brand-identity">
          {/* Design agent: Presents the logo in its pure form to spotlight the simplified network mark. */}
          <img
            className="brand-logo"
            id="brand-logo"
            src={`${process.env.PUBLIC_URL}/logo.svg`}
            alt="MasqIP tree network logo"
          />
          <div className="brand-text" id="brand-text">
            <h1 className="brand-title" id="brand-title">MasqIP Studio</h1>
            <p className="brand-subtitle" id="brand-subtitle">
              Modern IPv4 planning, summarization, and insight toolkit.
            </p>
          </div>
        </div>
      </header>

      <main className="spatial-stage" id="spatial-stage">
        {/* View 1: IP Inspector (Now first in DOM order for logical flow, though CSS controls visibility) */}
        <div className={`spatial-view ${activeTab === 'inspector' ? 'active' : 'inactive'}`}>
          <section className="tool-card" id="tool-card-inspector">
            <div className="card-header" id="card-header-inspector">
              <h2 id="card-title-inspector">IPv4 Insight Panel</h2>
              <p id="card-desc-inspector">
                Inspect a specific address to visualise position, availability, and classifications.
              </p>
            </div>
            <SafeMount
              component={IpInspector}
              name="IpInspector"
              componentProps={{
                onActivePrefixChange: setActivePrefix,
                initialState: bootState.inspector,
                onStateChange: setInspectorSnapshot,
              }}
            />
          </section>
        </div>

        {/* View 2: VLSM Calculator */}
        <div className={`spatial-view ${activeTab === 'plan' ? 'active' : 'inactive'}`}>
          <section className="tool-card" id="tool-card-plan">
            <div className="card-header" id="card-header-plan">
              <h2 id="card-title-plan">Hierarchical IPv4 Plan</h2>
              <p id="card-desc-plan">
                Configure a base network, then describe usable host counts for each subnet.
              </p>
            </div>
            <SafeMount
              component={VlsmCalculator}
              name="VlsmCalculator"
              componentProps={{
                initialState: bootState.vlsm,
                onStateChange: setVlsmSnapshot,
              }}
            />
          </section>
        </div>

        {/* View 3: About */}
        <div className={`spatial-view ${activeTab === 'about' ? 'active' : 'inactive'}`}>
          <SafeMount component={About} name="About" />
        </div>

        {/* View 4: Showcase */}
        <div className={`spatial-view ${activeTab === 'showcase' ? 'active' : 'inactive'}`}>
          <SafeMount component={Showcase} name="Showcase" />
        </div>

        {/* View 5: CIDR / mask / wildcard reference table (spec 003) */}
        <div className={`spatial-view ${activeTab === 'reference' ? 'active' : 'inactive'}`}>
          <section className="tool-card" id="tool-card-reference">
            <div className="card-header" id="card-header-reference">
              <h2 id="card-title-reference">CIDR Reference Table</h2>
              <p id="card-desc-reference">
                Every prefix from /0 to /32 with its dotted mask, wildcard, total addresses, and
                usable hosts.
              </p>
            </div>
            <SafeMount
              component={ReferenceTable}
              name="ReferenceTable"
              componentProps={{ activePrefix }}
            />
          </section>
        </div>
      </main>

      <NavigationDock activeTab={activeTab} onTabChange={setActiveTab} />
    </div>
  );
}

export default App;

